/* External */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { getCommunicationCarousel } from '../../services/communicationCarousel';
import Button from '../../components/button/Button';
import { mapHomeBannerPayload } from './mapHomeBannerPayload';

import './HomeBannerSlot.scss';

const PLACEHOLDER_ASSET = { __homeBannerPlaceholder: true };

/**
 * Sempre chama onFetched com um item não vazio para não travar o infinite scroll do Lander.
 */
function HomeBannerSlot({
  carouselId,
  onFetched,
  fetchEnabled,
  heroToastOverlay,
}) {
  const communicationPayloadRef = useRef(null);
  const onFetchedRef = useRef(onFetched);
  const [banner, setBanner] = useState(null);
  const [logoFailed, setLogoFailed] = useState(false);

  onFetchedRef.current = onFetched;

  useEffect(() => {
    let cancelled = false;

    if (!fetchEnabled) {
      queueMicrotask(() => {
        if (!cancelled) onFetchedRef.current([PLACEHOLDER_ASSET]);
      });
      return () => {
        cancelled = true;
      };
    }

    (async () => {
      const raw = await getCommunicationCarousel({ carouselId });
      if (cancelled) return;
      communicationPayloadRef.current = raw;
      setBanner(mapHomeBannerPayload(raw));
      onFetchedRef.current([PLACEHOLDER_ASSET]);
    })();

    return () => {
      cancelled = true;
    };
  }, [carouselId, fetchEnabled]);

  if (!banner) {
    return null;
  }

  const hasImage = Boolean(banner.backgroundUrl);
  const hasLogo = Boolean(banner.logoUrl) && !logoFailed;
  const hasCta = Boolean(banner.ctaText && banner.ctaLink);
  const containerStyle = {};
  if (hasImage) {
    containerStyle.backgroundImage = `url(${banner.backgroundUrl})`;
  } else if (banner.backgroundColor) {
    containerStyle.backgroundColor = banner.backgroundColor;
  }

  return (
    <section
      className={classNames('dtv-home-banner-slot', {
        'dtv-home-banner-slot--with-image': hasImage,
        'dtv-home-banner-slot--solid': !hasImage,
      })}
      style={containerStyle}
      aria-label={banner.title || 'Home banner'}
    >
      {hasImage && <div className="dtv-home-banner-slot__overlay" aria-hidden="true" />}
      {heroToastOverlay && (
        <div className="dtv-home-banner-slot__toast-layer">
          {heroToastOverlay}
        </div>
      )}
      <div
        className={classNames('dtv-home-banner-slot__content', {
          'dtv-home-banner-slot__content--with-image': hasImage,
          'dtv-home-banner-slot__content--solid': !hasImage,
        })}
      >
        {hasLogo && (
          <img
            src={banner.logoUrl}
            alt="logo"
            className="dtv-home-banner-slot__logo"
            onError={() => setLogoFailed(true)}
          />
        )}
        {banner.title && <h3 className="dtv-home-banner-slot__title">{banner.title}</h3>}
        {hasCta && (
          <a href={banner.ctaLink} className="dtv-home-banner-slot__cta-link">
            <Button variant="primary" className="dtv-home-banner-slot__cta-button">{banner.ctaText}</Button>
          </a>
        )}
      </div>
    </section>
  );
}

HomeBannerSlot.propTypes = {
  carouselId: PropTypes.string.isRequired,
  onFetched: PropTypes.func.isRequired,
  fetchEnabled: PropTypes.bool,
  heroToastOverlay: PropTypes.node,
};

HomeBannerSlot.defaultProps = {
  fetchEnabled: true,
  heroToastOverlay: undefined,
};

export default HomeBannerSlot;
